import React, { useState, useEffect } from 'react';
import styles from './SpellingProgress.module.css';
import { API_BASE_URL, apiRequest } from '../../config/api';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SpellingProgress = () => {
  const [sessionStats, setSessionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMode, setSelectedMode] = useState('word_completion');

  useEffect(() => {
    fetchSessionStats();
  }, []);

  const fetchSessionStats = async () => {
    try {
      setLoading(true);
      const stats = await apiRequest('/sessions/stats');
      setSessionStats(stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getChartData = (mode) => {
    if (!sessionStats || !sessionStats[mode] || sessionStats[mode].length === 0) {
      return null;
    }

    // Sort sessions by date
    const sortedSessions = [...sessionStats[mode]].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    // Format dates for x-axis
    const labels = sortedSessions.map(session => 
      new Date(session.date).toLocaleDateString()
    );

    // Extract data points
    const accuracyData = sortedSessions.map(session => session.accuracy);
    const responseTimeData = sortedSessions.map(session => session.averageResponseTime);

    return {
      labels,
      datasets: [
        {
          label: 'Accuracy (%)',
          data: accuracyData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          tension: 0.3,
          yAxisID: 'y'
        },
        {
          label: 'Response Time (s)',
          data: responseTimeData,
          borderColor: 'rgb(234, 88, 12)',
          backgroundColor: 'rgba(234, 88, 12, 0.5)',
          tension: 0.3,
          yAxisID: 'y1'
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    stacked: false,
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Percentage (%)',
        },
        min: 0,
        max: 100,
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Time (seconds)',
        },
        min: 0,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Progress Over Time',
      },
    },
  };

  const renderModeSelector = () => {
    const modes = [
      { id: 'word_completion', name: 'Word Completion' },
      { id: 'audio_spelling', name: 'Audio Spelling' },
      { id: 'typing_challenge', name: 'Typing Challenge' },
      { id: 'anagram_challenge', name: 'Anagram Challenge' },
      { id: 'definition_spelling', name: 'Definition Spelling' },
      { id: 'spelling_bee', name: 'Spelling Bee' },
      { id: 'story_spelling', name: 'Story Spelling' }
    ];

    return (
      <div className={styles.modeSelector}>
        <h3 className={styles.sectionTitle}>Select Mode</h3>
        <div className={styles.modeTabs}>
          {modes.map(mode => (
            <button
              key={mode.id}
              className={`${styles.modeTab} ${selectedMode === mode.id ? styles.active : ''}`}
              onClick={() => setSelectedMode(mode.id)}
            >
              {mode.name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderProgressSummary = (mode) => {
    if (!sessionStats || !sessionStats[mode] || sessionStats[mode].length === 0) {
      return (
        <div className={styles.noData}>
          <p>No data available for this mode yet. Complete some sessions to see your progress!</p>
        </div>
      );
    }

    // Get the first and most recent session
    const sortedSessions = [...sessionStats[mode]].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    const firstSession = sortedSessions[0];
    const latestSession = sortedSessions[sortedSessions.length - 1];

    // Calculate improvements
    const accuracyChange = latestSession.accuracy - firstSession.accuracy;
    const speedChange = firstSession.averageResponseTime - latestSession.averageResponseTime;

    return (
      <div className={styles.progressSummary}>
        <h3 className={styles.sectionTitle}>Progress Summary</h3>
        
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <div className={`${styles.summaryIcon} ${styles.iconBlue}`}>📈</div>
              <h4>Accuracy</h4>
            </div>
            <div className={styles.summaryContent}>
              <p className={styles.currentValue}>{latestSession.accuracy.toFixed(1)}%</p>
              <div className={`${styles.change} ${accuracyChange >= 0 ? styles.positive : styles.negative}`}>
                {accuracyChange >= 0 ? '↑' : '↓'} {Math.abs(accuracyChange).toFixed(1)}%
              </div>
            </div>
          </div>
          
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <div className={`${styles.summaryIcon} ${styles.iconOrange}`}>⏱️</div>
              <h4>Response Time</h4>
            </div>
            <div className={styles.summaryContent}>
              <p className={styles.currentValue}>{latestSession.averageResponseTime.toFixed(1)}s</p>
              <div className={`${styles.change} ${speedChange >= 0 ? styles.positive : styles.negative}`}>
                {speedChange >= 0 ? '↑' : '↓'} {Math.abs(speedChange).toFixed(1)}s
              </div>
            </div>
          </div>
          
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <div className={`${styles.summaryIcon} ${styles.iconPurple}`}>🎯</div>
              <h4>Sessions Completed</h4>
            </div>
            <div className={styles.summaryContent}>
              <p className={styles.currentValue}>{sortedSessions.length}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorPatterns = (mode) => {
    if (!sessionStats || !sessionStats[mode] || sessionStats[mode].length === 0) {
      return null;
    }

    // Get the most recent session
    const sortedSessions = [...sessionStats[mode]].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    const latestSession = sortedSessions[0];

    if (!latestSession.errorPatterns || latestSession.errorPatterns.length === 0) {
      return null;
    }

    return (
      <div className={styles.errorPatterns}>
        <h3 className={styles.sectionTitle}>Areas to Improve</h3>
        <div className={styles.patternsList}>
          {latestSession.errorPatterns.map((pattern, index) => (
            <div key={index} className={styles.patternCard}>
              <div className={styles.patternIcon}>🔍</div>
              <p>{pattern.pattern}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className={styles.loading}>Loading progress data...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const chartData = getChartData(selectedMode);

  return (
    <div className={styles.container}>
      {renderModeSelector()}
      
      {renderProgressSummary(selectedMode)}
      
      <div className={styles.chartSection}>
        <h3 className={styles.sectionTitle}>Progress Over Time</h3>
        {chartData ? (
          <div className={styles.chart}>
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div className={styles.noData}>
            <p>Not enough data to display progress chart. Complete more sessions to see your progress over time!</p>
          </div>
        )}
      </div>
      
      {renderErrorPatterns(selectedMode)}
    </div>
  );
};

export default SpellingProgress; 